// TOTP-Setup + Verify in einer Page.
// State-Maschine basiert auf MeResponse.totp_state:
//   - "none"     -> Setup-Flow: POST /totp/setup -> QR-URI + Backup-Codes -> Code -> /totp/confirm
//   - "pending"  -> wie "none" (Setup-Flow neu starten, alte Setup-Daten verworfen)
//   - "confirmed" -> Verify-Flow: Code -> /totp/verify
//
// Nach erfolgreichem /totp/verify oder /totp/confirm+Verify ist Cookie volle Session
// und useAuth liefert isAuthenticated=true → DashboardPage rendert.

import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import {
  TotpSetupResponse,
  totpConfirm,
  totpSetup,
  totpVerify,
  UnauthorizedError,
} from "../lib/api";
import { useAuth } from "../lib/auth";

type Mode = "loading" | "setup" | "verify" | "done";

export default function TotpPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [mode, setMode] = useState<Mode>("loading");
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Guard gegen Mehrfach-Calls (React Strict Mode + auth.me Re-Render
  // würden sonst setup_totp 2-3× rufen; jedes Mal ein neues Secret in DB).
  const setupCalled = useRef(false);

  useEffect(() => {
    if (auth.isLoading) return;
    if (!auth.me) {
      navigate("/login", { replace: true });
      return;
    }
    if (auth.me.totp_verified) {
      navigate("/", { replace: true });
      return;
    }
    if (auth.me.totp_state === "confirmed") {
      setMode("verify");
      return;
    }
    // 'none' oder 'pending' → Setup-Flow EINMAL anstoßen
    if (setupCalled.current) return;
    setupCalled.current = true;
    void (async () => {
      try {
        const data = await totpSetup();
        setSetupData(data);
        setMode("setup");
      } catch (e) {
        if (e instanceof UnauthorizedError) {
          navigate("/login", { replace: true });
          return;
        }
        setupCalled.current = false; // Retry erlauben
        setError(e instanceof Error ? e.message : "Setup fehlgeschlagen");
      }
    })();
  }, [auth.isLoading, auth.me, navigate]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === "setup") {
        await totpConfirm(code.trim());
        // Confirm setzt Cookie noch NICHT auf full session — danach verify
        await totpVerify(code.trim());
      } else if (mode === "verify") {
        await totpVerify(code.trim());
      }
      // Cookie ist jetzt volle Session
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setError("Code falsch oder abgelaufen.");
      } else {
        setError(err instanceof Error ? err.message : "Verifikation fehlgeschlagen");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === "loading") return <FullPage>Lade …</FullPage>;

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-slate-900/60 backdrop-blur card-glow-accent-blue p-6 sm:p-8">
        <h1 className="text-xl font-semibold mb-1 text-slate-100">Zwei-Faktor-Code</h1>
        <p className="text-xs text-slate-500 mb-5">{auth.me?.email}</p>

        {mode === "setup" && setupData && (
          <SetupBlock data={setupData} />
        )}
        {mode === "verify" && (
          <p className="text-sm text-slate-600 mb-4">
            Öffne deine Authenticator-App und gib den 6-stelligen Code ein.
          </p>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="code" className="block text-xs font-medium text-slate-400 mb-1">
              Code
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              minLength={6}
              maxLength={9}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950/50 text-slate-100 px-3 py-2 tabular-nums text-lg tracking-widest text-center placeholder-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              placeholder="123456"
              disabled={submitting}
              autoFocus
            />
            <p className="mt-1 text-xs text-slate-500">
              6 Ziffern — oder Backup-Code (8 Zeichen, mit Bindestrich).
            </p>
          </div>

          {error && (
            <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || code.trim().length < 6}
            className="w-full rounded-lg bg-sky-500 hover:bg-sky-400 disabled:opacity-40 disabled:cursor-not-allowed text-white py-2.5 font-medium transition"
          >
            {submitting ? "prüfe …" : mode === "setup" ? "Bestätigen + Login" : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}

function SetupBlock({ data }: { data: TotpSetupResponse }) {
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&bgcolor=0f172a&color=e2e8f0&data=${encodeURIComponent(
    data.provisioning_uri,
  )}`;
  return (
    <div className="mb-5 space-y-4">
      <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-300">
        Erstmaliges Setup. Scanne QR mit Authy / Google Authenticator / 1Password,
        speichere die Backup-Codes <strong>jetzt</strong>.
      </div>
      <div className="flex justify-center">
        <img src={qrSrc} alt="TOTP QR-Code" className="rounded-lg border border-slate-700" />
      </div>
      <details className="text-xs">
        <summary className="cursor-pointer text-slate-500 hover:text-slate-400">
          otpauth://-URL anzeigen
        </summary>
        <code className="mt-1 block break-all text-slate-400 bg-slate-950 border border-slate-800 p-2 rounded">
          {data.provisioning_uri}
        </code>
      </details>
      <div>
        <h3 className="text-sm font-semibold text-slate-200 mb-1">Backup-Codes</h3>
        <p className="text-xs text-slate-500 mb-2">
          Jeweils einmal verwendbar. Speichere sie sicher (1Password) —
          werden nur jetzt gezeigt.
        </p>
        <ul className="grid grid-cols-2 gap-1.5 text-sm font-mono">
          {data.backup_codes.map((c) => (
            <li
              key={c}
              className="bg-slate-950 border border-slate-700 text-slate-300 rounded px-2 py-1"
            >
              {c}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FullPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">
      {children}
    </div>
  );
}
